import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getEnvValue } from '../../../../Helper/env';
import lodashFindIndex from 'lodash/findIndex';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../../../Config/constants';
import MealPlanHelper from '../../../../Helper/MealPlan';
import useMealPlan from '../../../../Hooks/useMealPlan';
import useMealPlanHint from '../../../../Hooks/useMealPlanHint';
import { setMealPlanDay } from '../../../../RTK/mealPlan';
import {
  selectMealPlanCartByDay,
  selectMealPlanHint,
} from '../../../../RTK/mealPlan/selectors';

import { BreadcrumbsDay } from '../types';

enum PrevOrNext {
  PREV = 'prev',
  NEXT = 'next',
}
const RAZZLE_BUILD_MODE = getEnvValue('RAZZLE_BUILD_MODE');
const isBranded = RAZZLE_BUILD_MODE === 'branded';
const useMealPlanNavigation = (isMobileNav = false) => {
  const dispatch = useDispatch();
  const {
    isMealPlan,
    isMealPlanOnly,
    isSelectedDayHasCart,
    mealPlanCartCounter,
    mealPlanDay,
    mealPlanDayCount,
    validateMealPlan,
  } = useMealPlan();
  const {
    isMealPlanHintEnabled,
    maybeShowMealPlanFeaturedHint,
    maybeShowMealPlanNextDateHint,
    maybeShowMealPlanReviewHint,
    maybeShowMealPlanPrevDateHint,
    maybeShowMealPlanExclusiveHint,
    maybeShowMealPlanCreateHint,
  } = useMealPlanHint();
  const selectedDayCart = useSelector((state) =>
    selectMealPlanCartByDay(state, mealPlanDay?.value),
  );
  const hintSelector = useMemo(
    () => ({
      featured: selectMealPlanHint('isShownFeatured'),
      next: selectMealPlanHint('isShownNext'),
      nextShow: selectMealPlanHint('showNext'),
      review: selectMealPlanHint('isShownReview'),
      reviewShow: selectMealPlanHint('showReview'),
      prev: selectMealPlanHint('isShownPrev'),
      prevShow: selectMealPlanHint('showPrev'),
      exclusive: selectMealPlanHint('isShownExclusive'),
      create: selectMealPlanHint('isShownCreate'),
    }),
    [],
  );
  const isShownFeatured = useSelector(hintSelector.featured);
  const isShownNext = useSelector(hintSelector.next);
  const isNextShowing = useSelector(hintSelector.nextShow);
  const isShownReview = useSelector(hintSelector.review);
  const isReviewShowing = useSelector(hintSelector.nextShow);
  const isShownPrev = useSelector(hintSelector.prev);
  const isPrevShowing = useSelector(hintSelector.prevShow);
  const isShownExclusive = useSelector(hintSelector.exclusive);
  const isShownCreate = useSelector(hintSelector.create);

  const days: BreadcrumbsDay[] = useMemo(() => {
    if (mealPlanDayCount === 0) return [];
    const isShort = mealPlanDayCount === 3;
    const dateArr = isShort ? [1, 3, 5] : [1, 2, 3, 4, 5];
    return dateArr.map((day) =>
      MealPlanHelper.getDayValue(mealPlanDayCount, day),
    );
  }, [mealPlanDayCount]);

  const { selectedDayIndex, isFirstDay, isLastDay } = useMemo(() => {
    if (lodashIsEmpty(days)) {
      return { selectedDayIndex: 0, isFirstDay: true, isLastDay: false };
    }
    const dayIndex = lodashFindIndex(days, { value: mealPlanDay?.value });
    return {
      selectedDayIndex: dayIndex,
      isFirstDay: dayIndex === 0,
      isLastDay: dayIndex === days.length - 1,
    };
  }, [days, mealPlanDay?.value]);

  // effect for deciding when the hint will show
  useEffect(() => {
    let paramMealPlanType;
    if (isBranded) {
      const params = new URLSearchParams(document?.location?.search);
      paramMealPlanType = params.get('meal_plan_type');
    }
    if (
      isBranded && // must be on branded env
      isMealPlan && // meal plan type
      (isMealPlanOnly || paramMealPlanType) && // meal plan only store or has meal plan type on url
      !isShownFeatured // featured hint is not shown yet
    ) {
      // hint for featured item on branded it should trigger onload since order type and meal plan type is pass on url parameter
      maybeShowMealPlanFeaturedHint();
    } else if (
      !isShownNext && // next hint not shown yet
      isFirstDay && // and adding an item to first day (monday)
      selectedDayCart.length !== 0 // has no cart yet
    ) {
      // hint for next
      maybeShowMealPlanNextDateHint();
    } else if (
      isShownNext && // next tip is already shown
      !isNextShowing && // next tip is currently not showing
      !isShownReview // review tip is not shown yet
    ) {
      // hint for review (mobile)
      maybeShowMealPlanReviewHint();
    } else if (
      !isReviewShowing && // review tip is currently not showing
      !isShownPrev && // prev tip is not shown yet
      !isFirstDay && // not firt day anymore (if not first day, prev/back button is now visible)
      selectedDayCart.length === 0 // has no cart yet
    ) {
      // hint for prev
      maybeShowMealPlanPrevDateHint();
    } else if (
      !isPrevShowing && // prev tip is currently not showing
      !isShownExclusive && // exclusive tip is not shown yet
      mealPlanDay?.value >= constants.MEAL_PLAN_EXCLUSIVE_AVAILABILITY[0] // selected day is more than first day of exclusive availability
    ) {
      // hint for exclusive
      maybeShowMealPlanExclusiveHint();
    } else if (
      !isShownCreate && // create meal plan tip is not shown yet
      !isMobileNav && // must not be use this hook on mobile navigation because the create button is not visible if this is true
      isLastDay && // must be last day or friday
      selectedDayCart.length >= 1 // has cart
    ) {
      // hint for create
      maybeShowMealPlanCreateHint();
    }
  }, [
    isMealPlan,
    isMealPlanOnly,
    isFirstDay,
    isLastDay,
    isMobileNav,
    isShownFeatured,
    isNextShowing,
    isShownNext,
    isPrevShowing,
    isShownPrev,
    isReviewShowing,
    isShownReview,
    isShownCreate,
    isShownExclusive,
    mealPlanDay?.value,
    selectedDayCart,
    maybeShowMealPlanFeaturedHint,
    maybeShowMealPlanNextDateHint,
    maybeShowMealPlanReviewHint,
    maybeShowMealPlanPrevDateHint,
    maybeShowMealPlanExclusiveHint,
    maybeShowMealPlanCreateHint,
  ]);

  const _onBreadCrumbsPress = useCallback(
    (day: BreadcrumbsDay) => {
      const currentDay = days[selectedDayIndex];
      if (day.value !== currentDay.value) {
        dispatch(setMealPlanDay(day));
      }
    },
    [days, selectedDayIndex, dispatch],
  );

  const _onPrevOrNext = useCallback(
    (prevOrNext: PrevOrNext) => {
      const isNext = prevOrNext === PrevOrNext.NEXT;
      if (isNext) {
        const isCartValid = validateMealPlan();
        if (!isCartValid) return; // stop the code from going if current day cart is not valid
      }
      // must have atleast 1 item each day on this point before can next
      const newValue =
        days[isNext ? selectedDayIndex + 1 : selectedDayIndex - 1];
      dispatch(setMealPlanDay(newValue));
    },
    [days, selectedDayIndex, validateMealPlan, dispatch],
  );

  const _onPrev = useCallback(
    () => _onPrevOrNext(PrevOrNext.PREV),
    [_onPrevOrNext],
  );

  const _onNext = useCallback(
    () => _onPrevOrNext(PrevOrNext.NEXT),
    [_onPrevOrNext],
  );

  // when the meal plan hint is disabled AND not last day
  // effect for automatically change the meal plan day to next day once per navigation and if possible
  useEffect(() => {
    if (
      !isMealPlanHintEnabled &&
      !isLastDay &&
      !MealPlanHelper.mealPlanAutoNext[mealPlanDay?.value]
    ) {
      if (
        mealPlanCartCounter.current === constants.MEAL_PLAN_MAX_ITEM_PER_DAY
      ) {
        // current meal plan items is match the max item per day, auto next day
        MealPlanHelper.toggleMealPlanAutoNext(mealPlanDay?.value);
        setTimeout(_onNext, 250); // add little delay for ui smoothness
      }
    }
  }, [
    isLastDay,
    isMealPlanHintEnabled,
    mealPlanCartCounter,
    mealPlanDay?.value,
    selectedDayIndex,
    _onNext,
  ]);

  return {
    days,
    isDayHasCart: isSelectedDayHasCart,
    isFirstDay,
    isLastDay,
    selectedDayCart,
    selectedDayIndex,
    onBreadCrumbsPress: _onBreadCrumbsPress,
    onPrev: _onPrev,
    onNext: _onNext,
  };
};

export default useMealPlanNavigation;
